import { useMemo, useState } from "react";
import { Download, Folder, File as FileIcon } from "lucide-react";

import { Spinner } from "@/components/ui/Spinner";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { useEditorAvailability } from "@/features/doc_editor/hooks";
import { editorActionFor } from "@/features/doc_editor/types";

import { twoDriveApi } from "../api";
import { triggerBrowserDownload } from "../download";
import { isGisPreviewable } from "../gisPreview";
import { useSharedWithMe } from "../hooks";
import { DRIVE_SORT_KEYS, shareSortRow, sortBy, useSortState } from "../sorting";
import type { DriveShare } from "../types";
import { formatBytes, formatDateTime } from "../utils";
import { GisPreviewScreen } from "./GisPreviewScreen";
import { SortHeader } from "./SortHeader";

/**
 * Listing grid — header and rows share one class so the columns line up (each <li>
 * is its own grid, so only the name column may flex). DOM order: name, uploader,
 * created, modified, size, who shared it, access, download.
 */
const ROW_GRID =
  "grid items-center gap-3 grid-cols-[minmax(0,1fr)_4rem_1.75rem]" +
  " sm:grid-cols-[minmax(0,1fr)_5rem_8rem_4rem_1.75rem]" +
  " lg:grid-cols-[minmax(0,1fr)_9rem_11rem_5rem_8rem_4rem_1.75rem]" +
  " xl:grid-cols-[minmax(0,1fr)_9rem_9rem_11rem_5rem_8rem_4rem_1.75rem]";

const CELL_TEXT = "text-xs text-[var(--color-text-muted)]";
const HEAD_TEXT = "text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]";

interface Props {
  onOpenFolder: (id: string, name: string) => void;
}

export function SharedWithMeList({ onOpenFolder }: Props) {
  const { data: shares = [], isLoading } = useSharedWithMe();
  const [sort, toggleSort] = useSortState(DRIVE_SORT_KEYS);
  const rows = useMemo(() => sortBy(shares, shareSortRow, sort), [shares, sort]);
  // Plik w formacie znanym edytorowi otwiera się na pełnym ekranie (odczyt przy udostępnieniu
  // „view", edycja przy „edit"); reszta pobiera się jak dotąd.
  const { data: editorAvail } = useEditorAvailability();
  const editorExt = editorAvail?.available ? editorAvail.extensions ?? null : null;
  const [editorShare, setEditorShare] = useState<DriveShare | null>(null);
  const [gisShare, setGisShare] = useState<DriveShare | null>(null);
  const openFile = (share: DriveShare) => {
    if (isGisPreviewable(share.node_name)) setGisShare(share);
    else if (editorActionFor(share.node_name, editorExt)) setEditorShare(share);
    else downloadFile(share);
  };

  async function downloadFile(share: DriveShare) {
    const res = await twoDriveApi.download(share.node);
    if (res.url) triggerBrowserDownload(res.url, res.filename ?? share.node_name);
  }

  if (isLoading) {
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-[var(--color-text-muted)]">
        Nikt jeszcze nie udostępnił Ci plików.
      </div>
    );
  }

  return (
    <>
    {gisShare && (
      <GisPreviewScreen
        nodeId={gisShare.node}
        name={gisShare.node_name}
        onClose={() => setGisShare(null)}
        onDownload={() => downloadFile(gisShare)}
      />
    )}
    {editorShare && (
      <OdtEditorScreen
        kind="drive_file"
        id={editorShare.node}
        title={editorShare.node_name}
        onClose={() => setEditorShare(null)}
      />
    )}
    <ul className="divide-y divide-[var(--color-border)] border border-[var(--color-border)]">
      <li className={`${ROW_GRID} group px-3 py-1.5 bg-[var(--color-bg-base)]`}>
        <SortHeader label="Nazwa" sortKey="name" sort={sort} onSort={toggleSort} />
        <SortHeader
          label="Dodał/a"
          sortKey="uploader"
          sort={sort}
          onSort={toggleSort}
          className="hidden lg:inline-flex"
        />
        <SortHeader
          label="Utworzono"
          sortKey="created"
          sort={sort}
          onSort={toggleSort}
          className="hidden xl:inline-flex"
        />
        <SortHeader
          label="Ostatnia modyfikacja"
          sortKey="modified"
          sort={sort}
          onSort={toggleSort}
          className="hidden lg:inline-flex"
        />
        <SortHeader
          label="Rozmiar"
          sortKey="size"
          sort={sort}
          onSort={toggleSort}
          className="hidden sm:inline-flex justify-end"
        />
        <span className={`hidden sm:block truncate ${HEAD_TEXT}`}>Udostępnił/a</span>
        <span className={`truncate ${HEAD_TEXT}`}>Dostęp</span>
      </li>

      {rows.map((s) => {
        const isFolder = s.node_type === "folder";
        const uploader = s.node_uploaded_by_name || "—";
        return (
          <li key={s.id} className={`${ROW_GRID} px-3 py-2 hover:bg-[var(--color-bg-base)]`}>
            <button
              onClick={() => (isFolder ? onOpenFolder(s.node, s.node_name) : openFile(s))}
              className="flex items-center gap-2 min-w-0 text-left"
            >
              {isFolder ? (
                <Folder size={16} className="text-blue-500 flex-shrink-0" />
              ) : (
                <FileIcon size={16} className="text-[var(--color-text-muted)] flex-shrink-0" />
              )}
              <span className="min-w-0">
                <span className="block truncate text-sm">{s.node_name}</span>
                {/* Wąski ekran: dane z ukrytych kolumn schodzą pod nazwę. */}
                <span className="block text-[11px] leading-tight text-[var(--color-text-muted)] lg:hidden">
                  {!isFolder && <span className="sm:hidden">{formatBytes(s.node_size_bytes)} · </span>}
                  {uploader} · zm. {formatDateTime(s.node_updated_at)}
                  <span className="sm:hidden"> · od {s.shared_by_name}</span>
                </span>
              </span>
            </button>

            <span className={`hidden lg:block truncate ${CELL_TEXT}`} title={uploader}>
              {uploader}
            </span>
            <span className={`hidden xl:block truncate font-mono ${CELL_TEXT}`}>
              {formatDateTime(s.node_created_at)}
            </span>
            <span className={`hidden lg:block truncate font-mono ${CELL_TEXT}`}>
              {formatDateTime(s.node_updated_at)}
            </span>
            <span className={`hidden sm:block text-right font-mono ${CELL_TEXT}`}>
              {isFolder ? "" : formatBytes(s.node_size_bytes)}
            </span>
            <span className={`hidden sm:block truncate ${CELL_TEXT}`} title={s.shared_by_name}>
              od {s.shared_by_name}
            </span>
            <span className="text-[10px] font-mono uppercase text-[var(--color-text-muted)]">
              {s.access === "edit" ? "Edycja" : "Odczyt"}
            </span>
            {s.node_type === "file" ? (
              <button
                onClick={() => downloadFile(s)}
                className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                title="Pobierz"
              >
                <Download size={15} />
              </button>
            ) : (
              <span />
            )}
          </li>
        );
      })}
    </ul>
    </>
  );
}
